// © Microsoft Corporation. All rights reserved.

import { String } from "typescript-string-operations";

/**
 * Enum for supported date time format
 */
export enum SupportedLocalizedDateTimeFormat {
    Default,
    Medium,
    MonthDate,
    Weekday,
    Time
}

/**
 * contains utility functions to convert Date into string based on locale.
 */
export class LocalizationUtility {
    public supportsLocaleSpecifiers: boolean;
    private dateFormatOptionsMap: Map<SupportedLocalizedDateTimeFormat, Intl.DateTimeFormatOptions> | null = new Map();

    constructor() {
        this.setSupportsLocaleSpecifiers();
        // Populating map with different date-time formats
        this.dateFormatOptionsMap.set(SupportedLocalizedDateTimeFormat.Medium, { year: "numeric", month: "short", day: "numeric" });
        this.dateFormatOptionsMap.set(SupportedLocalizedDateTimeFormat.MonthDate, { month: "short", day: "numeric" });
        this.dateFormatOptionsMap.set(SupportedLocalizedDateTimeFormat.Weekday, { weekday: "short" });
        this.dateFormatOptionsMap.set(SupportedLocalizedDateTimeFormat.Time, { hour: "numeric", minute: "numeric" });
    }

    /**
     * override the date format options
     * @param format the supported localized Date time format enum
     * @param options the options to be override
     */
    public overrideDateFormat(format: SupportedLocalizedDateTimeFormat, options: any): void {
        if (format && options) {
            this.dateFormatOptionsMap.set(format, options);
        }
    }

    /**
     * Returns date in string form by formatting it based on locale.
     * Ex: 9/3/2010
     * @param date Date that needs to be converted
     * @param locale Locale to use
     */
    public localizedDateString(date: Date, locale: string): string {
        return this.formatDate(date, SupportedLocalizedDateTimeFormat.Default, locale);
    }

    /**
     * Returns date in string form by formatting it based on locale.
     * Ex: Sep 3, 2010
     * @param date Date that needs to be converted
     * @param locale Locale to use
     */
    public localizedDateStringMedium(date: Date, locale: string): string {
        return this.formatDate(date, SupportedLocalizedDateTimeFormat.Medium, locale);
    }

    /**
     * Returns month and date.
     * Ex: Sep 3
     * @param date Date that needs to be converted
     * @param locale Locale to use
     */
    public localizedMonthDate(date: Date, locale: string): string {
        return this.formatDate(date, SupportedLocalizedDateTimeFormat.MonthDate, locale);
    }

    /**
     * Returns week day.
     * Ex: Fri
     * @param date Date that needs to be converted
     * @param locale Locale to use
     */
    public localizedWeekday(date: Date, locale: string): string {
        return this.formatDate(date, SupportedLocalizedDateTimeFormat.Weekday, locale);
    }

    /**
     * Returns time.
     * Ex: 12:05 PM
     * @param date Date that needs to be converted
     * @param locale Locale to use
     */
    public formatTimeFromDate(date: Date, locale: string): string {
        if (this.supportsLocaleSpecifiers && this.dateFormatOptionsMap) {
            return date.toLocaleTimeString(locale, this.dateFormatOptionsMap.get(SupportedLocalizedDateTimeFormat.Time));
        }

        return date.toTimeString();
    }

    /**
     * Formats string of type `{0}h ago.`
     * @param locString string that needs to be formatted
     * @param params
     */
    public format(locString: string, ...params: Array<string>): string {
        if (!locString) {
            return "";
        }
        return String.Format(locString, ...params);
    }

    /**
     * Formats number depending on the locale.
     * Example -
     * 1000000 units in pt-BR will be formatted to 1.000.000
     * 1000000 units in en-US will be formatted to 1,000,000
     * 1000000 units in en-IN will be formatted to 10,00,000
     * @param num Number that needs to be formatted
     * @param locale Locale to use
     */
    public localizedNumberString(num: number, locale: string): string {
        if (this.supportsLocaleSpecifiers) {
            return num.toLocaleString(locale);
        }

        return num.toString();
    }

    /**
     * Formats Date based on the date time format and the locale.
     * @param date Date that needs to be formatted
     * @param format date time format map
     * @param locale Locale to use
     */
    private formatDate(date: Date, format: SupportedLocalizedDateTimeFormat, locale: string): string {

        let resultDateStr: string = null;

        // IE11 generates LEFT-TO-RIGHT and RIGHT-TO-LEFT marks that will html mismatch Server Side Rendered html result.
        // Note that this does not solve all languages since IE11 have different format strings (eg. zh-cn, ar-eg).
        // TODO: Fix format string for all languages.
        // Tracking workitem: https://dev.azure.com/microsoft/OS/_workitems/edit/25518259
        if (this.supportsLocaleSpecifiers) {
            if (format === SupportedLocalizedDateTimeFormat.Default) {
                resultDateStr = date.toLocaleDateString(locale);
            } else if (this.dateFormatOptionsMap) {
                const options = this.dateFormatOptionsMap.get(format);
                resultDateStr = date.toLocaleDateString(locale, options);
            }
        }

        if (resultDateStr === null) {
            resultDateStr = date.toDateString();
        }

        if (resultDateStr !== null) {
            return resultDateStr.replace(/\u200E/g, "").replace(/\u200F/g, "");
        }

        return resultDateStr;
    }

    /**
     * Sets the locale specifier flag
     */
    private setSupportsLocaleSpecifiers(): void {
        // Safari and IE 10 do not correctly implement toLocaleString when locale is specified.
        const toLocaleStringSupportsLocales = () => {
            try {
                const num = 0;
                num.toLocaleString("i");
            } catch (e) {
                return e.name === "RangeError";
            }

            return false;
        };

        this.supportsLocaleSpecifiers = toLocaleStringSupportsLocales();
    }
}